<template>
  <div style="text-align:left;">
    <TopNav />
    <div class="column-left">
      <SideNav />
    </div>

    <div class="column-right">
      <div class="main-container order-admin-page">
        <div v-if="loading" class="order-admin-loader">
          <div class="spinner-border text-secondary" role="status"></div>
          <span>Cargando orden...</span>
        </div>

        <div v-else-if="error" class="alert alert-danger">
          {{ error }}
        </div>

        <template v-else-if="orden">
          <header class="order-admin-header">
            <div class="header-main">
              <button type="button" class="back-button" @click="$router.back()">
                <b-icon icon="arrow-left" />
              </button>
              <div>
                <div class="eyebrow">Detalle operativo</div>
                <h1>Orden #{{ orden.id }}</h1>
                <div class="header-meta">
                  <span :class="['status-badge', orderStatusClass]">{{ orden.estado_label }}</span>
                  <span>{{ formatDateTime(orden.fecha_creado_iso) }}</span>
                  <router-link
                    v-if="orden.solicitud_cliente_id"
                    class="header-link"
                    :to="solicitudDetallePath"
                  >
                    Solicitud #{{ orden.solicitud_cliente_id }}
                  </router-link>
                </div>
              </div>
            </div>
            <div class="header-actions">
              <router-link class="action-button secondary" :to="`/ModificarOrden/${orden.id}`">
                <b-icon icon="pencil-square" />
                Modificar
              </router-link>
              <router-link class="action-button secondary" :to="`/ModificarHorario/${orden.id}`">
                <b-icon icon="calendar2-week" />
                Reasignar
              </router-link>
              <router-link v-if="profesional" class="action-button primary" :to="`/VerProfesional/${profesional.id}`">
                <b-icon icon="person-badge" />
                Profesional
              </router-link>
            </div>
          </header>

          <section class="summary-grid">
            <div class="summary-card">
              <div class="summary-icon customer"><b-icon icon="person" /></div>
              <div class="summary-copy">
                <span class="summary-label">Cliente</span>
                <strong>{{ customerName }}</strong>
                <span>{{ customerContact }}</span>
              </div>
            </div>
            <div class="summary-card">
              <div class="summary-icon professional"><b-icon icon="person-badge" /></div>
              <div class="summary-copy">
                <span class="summary-label">Profesional</span>
                <strong>{{ professionalName }}</strong>
                <span>{{ professionalContact }}</span>
              </div>
            </div>
            <div class="summary-card">
              <div class="summary-icon schedule"><b-icon icon="calendar2-week" /></div>
              <div class="summary-copy">
                <span class="summary-label">Agendamiento</span>
                <strong>{{ formatDate(orden.fecha_iso) }}</strong>
                <span>{{ scheduleLabel }}</span>
              </div>
            </div>
            <div class="summary-card">
              <div class="summary-icon money"><b-icon icon="cash-stack" /></div>
              <div class="summary-copy">
                <span class="summary-label">Monto</span>
                <strong>{{ formatCurrency(orden.monto_total || orden.monto) }}</strong>
                <span>Pagado: {{ formatCurrency(orden.monto_pagado) }}</span>
              </div>
            </div>
          </section>

          <div class="content-grid">
            <main class="content-main">
              <section class="panel">
                <div class="panel-header">
                  <h2><span class="panel-title-icon timeline-icon"><b-icon icon="clock-history" /></span>Linea de tiempo</h2>
                </div>
                <div v-if="timeline.length" class="timeline">
                  <div v-for="event in timeline" :key="event.key" class="timeline-item">
                    <div class="timeline-dot" :class="event.kind"></div>
                    <div>
                      <div class="timeline-title">{{ event.title }}</div>
                      <div class="timeline-date">{{ formatDateTime(event.date) }}</div>
                      <p v-if="event.description">{{ event.description }}</p>
                    </div>
                  </div>
                </div>
                <div v-else class="empty-state">Sin eventos registrados.</div>
              </section>

              <section v-if="solicitud" class="panel">
                <div class="panel-header">
                  <h2><span class="panel-title-icon conversation-icon"><b-icon icon="chat-dots" /></span>Conversacion de la solicitud</h2>
                  <span>{{ solicitudConversationMessageCount }} mensaje{{ solicitudConversationMessageCount === 1 ? '' : 's' }}</span>
                </div>
                <div v-if="solicitudConversationTabs.length > 1" class="conversation-tabs" role="tablist" aria-label="Conversaciones por profesional">
                  <button
                    v-for="conversation in solicitudConversationTabs"
                    :key="conversation.key"
                    type="button"
                    class="conversation-tab"
                    :class="{ active: activeSolicitudConversationKeyResolved === conversation.key }"
                    @click="setActiveSolicitudConversation(conversation.key)"
                  >
                    <span>{{ conversationLabel(conversation) }}</span>
                    <small>{{ conversation.message_count || (conversation.mensajes || []).length }}</small>
                  </button>
                </div>
                <div v-if="activeSolicitudMensajes.length" class="request-conversation">
                  <article
                    v-for="mensaje in activeSolicitudMensajes"
                    :key="mensaje.id"
                    class="conversation-message"
                    :class="messageBubbleClass(mensaje)"
                  >
                    <div class="message-heading">
                      <span>{{ messageSenderLabel(mensaje) }}</span>
                      <time>{{ formatDateTime(mensaje.fecha_iso) }}</time>
                    </div>
                    <p>{{ messageText(mensaje) }}</p>
                    <div v-if="messagePhotos(mensaje).length" class="message-photos">
                      <a
                        v-for="foto in messagePhotos(mensaje)"
                        :key="`msg-${mensaje.id}-foto-${foto.id}`"
                        :href="foto.link || foto.url"
                        target="_blank"
                        rel="noopener"
                      >
                        <b-icon icon="image" />
                        Foto #{{ foto.id }}
                      </a>
                    </div>
                    <div v-if="mensaje.mensaje_original && mensaje.mensaje_original !== mensaje.mensaje" class="message-original">
                      Original: {{ mensaje.mensaje_original }}
                    </div>
                  </article>
                </div>
                <div v-else class="empty-state">La solicitud no tiene conversación registrada.</div>
              </section>

              <section class="panel">
                <div class="panel-header">
                  <h2><span class="panel-title-icon services-icon"><b-icon icon="list-check" /></span>Servicios e items</h2>
                  <span>{{ servicios.length }} item{{ servicios.length === 1 ? '' : 's' }}</span>
                </div>
                <div v-if="servicios.length" class="table-wrap">
                  <table class="admin-table">
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th>Cantidad</th>
                        <th class="text-right">Precio</th>
                        <th>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="servicio in servicios" :key="servicio.id">
                        <td>
                          <strong>{{ servicio.nombre || 'Item' }}</strong>
                          <small v-if="servicio.descripcion">{{ servicio.descripcion }}</small>
                          <small v-if="servicio.nota_al_margen">Nota: {{ servicio.nota_al_margen }}</small>
                        </td>
                        <td>{{ servicio.cantidad || 1 }}</td>
                        <td class="text-right">{{ formatCurrency(servicio.precio) }}</td>
                        <td>
                          <span :class="['mini-badge', serviceDone(servicio) ? 'success' : 'muted']">
                            {{ serviceDone(servicio) ? 'Realizado' : 'Pendiente' }}
                          </span>
                          <span v-if="servicio.es_presupuesto" class="mini-badge info">Presupuesto</span>
                          <span v-if="servicio.es_insumo" class="mini-badge warning">Insumo</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-else class="empty-state">Sin servicios registrados.</div>
              </section>

              <section class="panel">
                <div class="panel-header">
                  <h2><span class="panel-title-icon visits-icon"><b-icon icon="calendar-check" /></span>Visitas</h2>
                  <span>{{ visitas.length }}</span>
                </div>
                <div v-if="visitas.length" class="stack-list">
                  <div v-for="visita in visitas" :key="visita.id" class="list-row">
                    <div>
                      <strong>{{ formatDate(visita.fecha_) }}</strong>
                      <span>{{ formatMinutes(visita.desde) }} - {{ formatMinutes(visita.hasta) }}</span>
                    </div>
                    <span :class="['mini-badge', visitStatusClass(visita.estado)]">{{ visitStatusLabel(visita.estado) }}</span>
                  </div>
                </div>
                <div v-else class="empty-state">Sin visitas registradas.</div>
              </section>

              <section class="panel">
                <div class="panel-header">
                  <h2><span class="panel-title-icon budget-icon"><b-icon icon="receipt" /></span>Presupuestos</h2>
                  <span>{{ presupuestos.length }}</span>
                </div>
                <div v-if="presupuestos.length" class="stack-list">
                  <div v-for="presupuesto in presupuestos" :key="presupuesto.id" class="list-row budget-row">
                    <div>
                      <strong>{{ presupuesto.nombre || presupuesto.descripcion || `Presupuesto #${presupuesto.id}` }}</strong>
                      <span>Creado {{ formatDateTime(presupuesto.fecha_creado_iso) }}</span>
                      <span v-if="presupuesto.fecha_enviado_iso">Enviado {{ formatDateTime(presupuesto.fecha_enviado_iso) }}</span>
                    </div>
                    <div class="row-right">
                      <strong>{{ formatCurrency(presupuesto.monto_total || budgetTotal(presupuesto)) }}</strong>
                      <span :class="['mini-badge', budgetStatusClass(presupuesto.estado)]">{{ budgetStatusLabel(presupuesto.estado) }}</span>
                    </div>
                  </div>
                </div>
                <div v-else class="empty-state">Sin presupuestos asociados.</div>
              </section>
            </main>

            <aside class="content-side">
              <section class="panel contact-panel">
                <div class="panel-header">
                  <h2><span class="panel-title-icon customer-icon"><b-icon icon="person-lines-fill" /></span>Cliente</h2>
                </div>
                <div class="contact-name">{{ customerName }}</div>
                <a v-if="cliente && cliente.email" :href="`mailto:${cliente.email}`">{{ cliente.email }}</a>
                <a v-if="cliente && cliente.telefono" :href="`tel:${cliente.telefono}`">{{ cliente.telefono }}</a>
                <div class="detail-line">
                  <span>Direccion</span>
                  <strong>{{ fullAddress }}</strong>
                </div>
                <router-link
                  v-if="cliente"
                  class="inline-link"
                  :to="`/superadmin/Usuarios/${cliente.id}`"
                >
                  Ver detalle de usuario
                </router-link>
              </section>

              <section class="panel contact-panel">
                <div class="panel-header">
                  <h2><span class="panel-title-icon professional-icon"><b-icon icon="person-badge" /></span>Profesional</h2>
                </div>
                <div class="contact-name">{{ professionalName }}</div>
                <a v-if="profesional && profesional.email" :href="`mailto:${profesional.email}`">{{ profesional.email }}</a>
                <a v-if="profesional && profesional.telefono" :href="`tel:${profesional.telefono}`">{{ profesional.telefono }}</a>
                <div class="flag-list">
                  <span v-if="profesional && profesional.suspendido" class="mini-badge danger">Suspendido</span>
                  <span v-if="profesional && profesional.oculto" class="mini-badge warning">Oculto</span>
                  <span v-if="profesional && profesional.visibilidad_restringida" class="mini-badge warning">Restringido</span>
                </div>
              </section>

              <section class="panel">
                <div class="panel-header">
                  <h2><span class="panel-title-icon payments-icon"><b-icon icon="credit-card" /></span>Pagos</h2>
                  <span>{{ pagos.length }}</span>
                </div>
                <div v-if="pagos.length" class="stack-list compact">
                  <div v-for="pago in pagos" :key="pago.id" class="list-row">
                    <div>
                      <strong>Pago #{{ pago.id }} - {{ formatCurrency(pago.monto) }}</strong>
                      <span>{{ formatDateTime(pago.fecha_creado_iso) }}</span>
                      <span v-if="pago.metodo_pago">Metodo: {{ pago.metodo_pago }}</span>
                    </div>
                    <span :class="['mini-badge', paymentStatusClass(pago.estado)]">{{ paymentStatusLabel(pago.estado) }}</span>
                  </div>
                </div>
                <div v-else class="empty-state">Sin pagos.</div>
              </section>

              <section class="panel">
                <div class="panel-header">
                  <h2><span class="panel-title-icon refunds-icon"><b-icon icon="arrow-counterclockwise" /></span>Reembolsos</h2>
                  <span>{{ reembolsos.length }}</span>
                </div>
                <div v-if="reembolsos.length" class="stack-list compact">
                  <div v-for="reembolso in reembolsos" :key="reembolso.id" class="list-row refund-row">
                    <div>
                      <strong>Reembolso #{{ reembolso.id }} - {{ formatCurrency(reembolso.monto_total_cliente) }}</strong>
                      <span>{{ refundPaymentIdsLabel(reembolso) }}</span>
                      <span>Creado {{ formatDateTime(reembolso.created_at) }}</span>
                      <span v-if="reembolso.pagado_at">Pagado {{ formatDateTime(reembolso.pagado_at) }}</span>
                      <span v-if="reembolso.motivo">Motivo: {{ reembolso.motivo }}</span>
                    </div>
                    <span :class="['mini-badge', refundStatusClass(reembolso.estado)]">{{ refundStatusLabel(reembolso) }}</span>
                  </div>
                </div>
                <div v-else class="empty-state">Sin reembolsos.</div>
              </section>

              <section class="panel">
                <div class="panel-header">
                  <h2><span class="panel-title-icon comments-icon"><b-icon icon="chat-left-text" /></span>Comentarios internos</h2>
                  <span>{{ comentarios.length }}</span>
                </div>
                <div v-if="comentarios.length" class="comments-list">
                  <article v-for="comentario in comentarios" :key="comentario.id" class="comment-card">
                    <div>
                      <strong>{{ authorName(comentario) }}</strong>
                      <span>{{ formatCommentDate(comentario.fecha) }}</span>
                    </div>
                    <p>{{ comentario.texto }}</p>
                  </article>
                </div>
                <div v-else class="empty-state">Sin comentarios.</div>
              </section>

              <section v-if="solicitud" class="panel">
                <div class="panel-header">
                  <h2><span class="panel-title-icon request-icon"><b-icon icon="inbox" /></span>Solicitud origen</h2>
                </div>
                <div class="detail-line">
                  <span>Titulo</span>
                  <router-link class="inline-link" :to="solicitudDetallePath">
                    {{ solicitud.titulo || `Solicitud #${solicitud.id || orden.solicitud_cliente_id}` }}
                  </router-link>
                </div>
                <div class="detail-line">
                  <span>Clase</span>
                  <strong>{{ solicitud.clase || 'Sin clase' }}</strong>
                </div>
                <p v-if="solicitud.descripcion" class="description-text">{{ solicitud.descripcion }}</p>
              </section>

              <section class="panel">
                <div class="panel-header">
                  <h2><span class="panel-title-icon files-icon"><b-icon icon="file-earmark-image" /></span>Archivos y fotos</h2>
                  <span>{{ archivos.length + fotos.length }}</span>
                </div>
                <div v-if="archivos.length || fotos.length" class="asset-list">
                  <a v-for="foto in fotos" :key="`foto-${foto.id}`" :href="foto.link" target="_blank" rel="noopener">
                    <b-icon icon="image" />
                    Foto #{{ foto.id }}
                  </a>
                  <span v-for="archivo in archivos" :key="`archivo-${archivo.id}`">
                    <b-icon icon="file-earmark" />
                    {{ archivo.nombre || `Archivo #${archivo.id}` }}
                  </span>
                </div>
                <div v-else class="empty-state">Sin adjuntos.</div>
              </section>
            </aside>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import SideNav from '@/components/SideNav.vue'
import TopNav from '@/components/TopNav.vue'

export default {
  name: 'OrdenDetalleAdmin',
  components: {
    SideNav,
    TopNav
  },
  data () {
    return {
      loading: true,
      error: '',
      payload: null,
      activeSolicitudConversationKey: null
    }
  },
  computed: {
    orden () {
      return this.payload && this.payload.orden ? this.payload.orden : null
    },
    cliente () {
      return this.payload ? this.payload.cliente : null
    },
    profesional () {
      return this.payload ? this.payload.profesional : null
    },
    solicitud () {
      return this.payload ? this.payload.solicitud : null
    },
    servicios () {
      return this.payload && Array.isArray(this.payload.servicios) ? this.payload.servicios : []
    },
    visitas () {
      return this.payload && Array.isArray(this.payload.visitas) ? this.payload.visitas : []
    },
    presupuestos () {
      return this.payload && Array.isArray(this.payload.presupuestos) ? this.payload.presupuestos : []
    },
    pagos () {
      return this.payload && Array.isArray(this.payload.pagos) ? this.payload.pagos : []
    },
    reembolsos () {
      return this.payload && Array.isArray(this.payload.reembolsos) ? this.payload.reembolsos : []
    },
    comentarios () {
      return this.payload && Array.isArray(this.payload.comentarios) ? this.payload.comentarios : []
    },
    archivos () {
      return this.payload && Array.isArray(this.payload.archivos) ? this.payload.archivos : []
    },
    fotos () {
      return this.payload && Array.isArray(this.payload.fotos) ? this.payload.fotos : []
    },
    solicitudMensajes () {
      const mensajes = this.payload && Array.isArray(this.payload.solicitud_mensajes)
        ? this.payload.solicitud_mensajes
        : []
      return mensajes.slice().sort((a, b) => new Date(a.fecha_iso || 0) - new Date(b.fecha_iso || 0))
    },
    solicitudConversationTabs () {
      const conversaciones = this.payload && Array.isArray(this.payload.solicitud_conversaciones)
        ? this.payload.solicitud_conversaciones
        : []
      if (conversaciones.length) {
        return conversaciones.map((conversation, index) => ({
          ...conversation,
          key: conversation.key || `conversation-${conversation.profesional_id || index}`,
          mensajes: Array.isArray(conversation.mensajes) ? conversation.mensajes : []
        })).filter(conversation => conversation.mensajes.length)
      }
      if (!this.solicitudMensajes.length) return []
      return [{
        key: this.orden && this.orden.profesional_id ? `prof-${this.orden.profesional_id}` : 'conversation-actual',
        profesional_id: this.orden ? this.orden.profesional_id : null,
        profesional: this.profesional,
        mensajes: this.solicitudMensajes,
        message_count: this.solicitudMensajes.length,
        is_orden_profesional: true
      }]
    },
    activeSolicitudConversationKeyResolved () {
      if (!this.solicitudConversationTabs.length) return null
      const activeKey = this.activeSolicitudConversationKey
      if (activeKey && this.solicitudConversationTabs.some(conversation => conversation.key === activeKey)) {
        return activeKey
      }
      const ordenProfessionalKey = this.orden && this.orden.profesional_id ? `prof-${this.orden.profesional_id}` : null
      if (ordenProfessionalKey && this.solicitudConversationTabs.some(conversation => conversation.key === ordenProfessionalKey)) {
        return ordenProfessionalKey
      }
      return this.solicitudConversationTabs[0].key
    },
    activeSolicitudConversation () {
      const key = this.activeSolicitudConversationKeyResolved
      return this.solicitudConversationTabs.find(conversation => conversation.key === key) || null
    },
    activeSolicitudMensajes () {
      const mensajes = this.activeSolicitudConversation && Array.isArray(this.activeSolicitudConversation.mensajes)
        ? this.activeSolicitudConversation.mensajes
        : []
      return mensajes.slice().sort((a, b) => new Date(a.fecha_iso || 0) - new Date(b.fecha_iso || 0))
    },
    solicitudConversationMessageCount () {
      const messageIds = new Set()
      this.solicitudConversationTabs.forEach(conversation => {
        ;(conversation.mensajes || []).forEach(mensaje => {
          messageIds.add(mensaje && mensaje.id ? mensaje.id : `${conversation.key}-${messageIds.size}`)
        })
      })
      return messageIds.size
    },
    customerName () {
      if (this.cliente) {
        const name = `${this.cliente.nombre || ''} ${this.cliente.apellidos || ''}`.trim()
        if (name) return name
      }
      return `${this.orden.nombre || ''} ${this.orden.apellido1 || ''}`.trim() || 'Cliente sin nombre'
    },
    customerContact () {
      return (this.cliente && (this.cliente.email || this.cliente.telefono)) || this.orden.telefono || 'Sin contacto'
    },
    professionalName () {
      if (!this.profesional) return 'Sin profesional'
      return `${this.profesional.nombre || ''} ${this.profesional.apellido || ''}`.trim() || `Profesional #${this.profesional.id}`
    },
    professionalContact () {
      return this.profesional ? (this.profesional.email || this.profesional.telefono || 'Sin contacto') : 'Sin contacto'
    },
    scheduleLabel () {
      const from = this.formatMinutes(this.orden.desde)
      const to = this.formatMinutes(this.orden.hasta)
      if (from && to) return `${from} - ${to}`
      return 'Sin horario'
    },
    fullAddress () {
      const parts = [
        this.orden.direccion,
        this.orden.n_depto ? `Depto/Casa ${this.orden.n_depto}` : '',
        this.orden.comuna && this.orden.comuna.nombre ? this.orden.comuna.nombre : ''
      ].filter(Boolean)
      return parts.join(', ') || 'Sin direccion'
    },
    solicitudDetallePath () {
      const solicitudId = this.solicitud && this.solicitud.id
        ? this.solicitud.id
        : this.orden.solicitud_cliente_id
      return `/SolicitudDetalleAdmin/${solicitudId}`
    },
    orderStatusClass () {
      const classes = {
        0: 'muted',
        1: 'info',
        2: 'primary',
        3: 'warning',
        4: 'success',
        10: 'danger'
      }
      return classes[this.orden.estado] || 'muted'
    },
    timeline () {
      const events = []
      if (!this.orden) return events
      this.addTimeline(events, 'created', 'Orden creada', this.orden.fecha_creado_iso, this.customerName, 'info')
      this.addTimeline(events, 'scheduled', 'Atencion agendada', this.orden.fecha_iso, this.scheduleLabel, 'primary')
      this.visitas.forEach(visita => {
        this.addTimeline(events, `visita-${visita.id}`, `Visita ${this.visitStatusLabel(visita.estado)}`, visita.fecha_, `${this.formatMinutes(visita.desde)} - ${this.formatMinutes(visita.hasta)}`, 'primary')
      })
      this.presupuestos.forEach(presupuesto => {
        this.addTimeline(events, `pres-${presupuesto.id}`, `Presupuesto ${this.budgetStatusLabel(presupuesto.estado)}`, presupuesto.fecha_creado_iso, presupuesto.nombre || `#${presupuesto.id}`, 'warning')
        if (presupuesto.fecha_enviado_iso) {
          this.addTimeline(events, `pres-sent-${presupuesto.id}`, 'Presupuesto enviado', presupuesto.fecha_enviado_iso, presupuesto.nombre || `#${presupuesto.id}`, 'warning')
        }
        if (presupuesto.fecha_aprobado_iso) {
          this.addTimeline(events, `pres-approved-${presupuesto.id}`, 'Presupuesto aprobado', presupuesto.fecha_aprobado_iso, this.formatCurrency(presupuesto.monto_total), 'success')
        }
      })
      this.pagos.forEach(pago => {
        this.addTimeline(events, `pago-${pago.id}`, `Pago #${pago.id} ${this.paymentStatusLabel(pago.estado)}`, pago.fecha_pagado_iso || pago.fecha_creado_iso, this.formatCurrency(pago.monto), 'success')
      })
      this.reembolsos.forEach(reembolso => {
        this.addTimeline(events, `reembolso-${reembolso.id}`, `Reembolso #${reembolso.id} ${this.refundStatusLabel(reembolso)}`, reembolso.pagado_at || reembolso.created_at, this.formatCurrency(reembolso.monto_total_cliente), 'danger')
      })
      this.comentarios.slice(0, 8).forEach(comentario => {
        this.addTimeline(events, `comentario-${comentario.id}`, 'Comentario interno', this.commentDateToIso(comentario.fecha), comentario.texto, 'muted')
      })
      this.addTimeline(events, 'completed', 'Orden completada', this.orden.fecha_completado_iso, '', 'success')
      return events.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))
    }
  },
  watch: {
    '$route.params.id': {
      immediate: true,
      handler () {
        this.fetchOrder()
      }
    }
  },
  methods: {
    fetchOrder () {
      const id = this.$route.params.id
      this.loading = true
      this.error = ''
      axios.get(`${window.hostname}api/admin/ordenes/${id}/detalle`)
        .then(response => {
          this.payload = response.data
          this.activeSolicitudConversationKey = null
        })
        .catch(error => {
          console.error('Error loading order detail', error)
          this.error = error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'No se pudo cargar la orden.'
        })
        .finally(() => {
          this.loading = false
        })
    },
    addTimeline (events, key, title, date, description, kind) {
      if (!date) return
      events.push({ key, title, date, description, kind })
    },
    setActiveSolicitudConversation (key) {
      this.activeSolicitudConversationKey = key
    },
    conversationLabel (conversation) {
      if (!conversation) return 'Conversacion'
      const professional = conversation.profesional || {}
      const name = `${professional.nombre || ''} ${professional.apellido || ''}`.trim()
      if (name) return name
      if (conversation.profesional_id) return `Profesional #${conversation.profesional_id}`
      return 'General'
    },
    formatCurrency (value) {
      const number = Number(value) || 0
      return number.toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 })
    },
    formatDate (value) {
      if (!value) return 'Sin fecha'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return 'Sin fecha'
      return date.toLocaleDateString('es-CL', { year: 'numeric', month: 'short', day: 'numeric' })
    },
    formatDateTime (value) {
      if (!value) return 'Sin fecha'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return 'Sin fecha'
      return date.toLocaleString('es-CL', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    },
    formatMinutes (value) {
      if (value === null || value === undefined || value === '') return ''
      const total = Number(value)
      if (!Number.isFinite(total)) return ''
      const hours = Math.floor(total / 60)
      const minutes = total % 60
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
    },
    serviceDone (servicio) {
      return Boolean(servicio && (servicio.realizado || servicio.fecha_realizado))
    },
    visitStatusLabel (estado) {
      const labels = {
        0: 'Pendiente',
        1: 'Agendada',
        2: 'Confirmada',
        3: 'En proceso',
        4: 'Terminada',
        10: 'Anulada'
      }
      return labels[estado] || `Estado ${estado}`
    },
    visitStatusClass (estado) {
      const classes = {
        0: 'muted',
        1: 'info',
        2: 'primary',
        3: 'warning',
        4: 'success',
        10: 'danger'
      }
      return classes[estado] || 'muted'
    },
    budgetStatusLabel (estado) {
      const labels = {
        0: 'Pendiente',
        1: 'Enviado',
        2: 'Aceptado',
        3: 'Completado',
        10: 'Rechazado'
      }
      return labels[estado] || `Estado ${estado}`
    },
    budgetStatusClass (estado) {
      const classes = {
        0: 'muted',
        1: 'info',
        2: 'success',
        3: 'primary',
        10: 'danger'
      }
      return classes[estado] || 'muted'
    },
    paymentStatusLabel (estado) {
      const labels = {
        0: 'Pendiente',
        1: 'Pagado',
        2: 'Reversado',
        3: 'En revision'
      }
      return labels[estado] || `Estado ${estado}`
    },
    paymentStatusClass (estado) {
      const classes = {
        0: 'warning',
        1: 'success',
        2: 'danger',
        3: 'info'
      }
      return classes[estado] || 'muted'
    },
    refundStatusLabel (reembolso) {
      if (!reembolso) return 'Sin estado'
      const labels = {
        pending_customer_details: 'Pendiente datos cliente',
        ready_to_pay: 'Listo para pagar',
        paid: 'Pagado',
        canceled: 'Cancelado'
      }
      return reembolso.estado_label || labels[reembolso.estado] || reembolso.estado || 'Sin estado'
    },
    refundStatusClass (estado) {
      const classes = {
        pending_customer_details: 'warning',
        ready_to_pay: 'info',
        paid: 'success',
        canceled: 'danger'
      }
      return classes[estado] || 'muted'
    },
    refundPaymentIdsLabel (reembolso) {
      const pagos = reembolso && Array.isArray(reembolso.pagos) ? reembolso.pagos : []
      const ids = pagos.map(pago => pago.pago_id).filter(Boolean)
      if (!ids.length) return 'Sin pagos asociados'
      return `Pago${ids.length === 1 ? '' : 's'} asociado${ids.length === 1 ? '' : 's'}: ${ids.map(id => `#${id}`).join(', ')}`
    },
    budgetTotal (presupuesto) {
      if (!presupuesto || !presupuesto.desgloce) return 0
      return Number(presupuesto.desgloce.total) || 0
    },
    messageSenderLabel (mensaje) {
      const labels = {
        cliente: 'Cliente',
        admin_propiedad: 'Admin. propiedad',
        profesional: 'Profesional',
        administrador: 'Administrador'
      }
      return labels[mensaje && mensaje.sender_type] || 'Sistema'
    },
    messageBubbleClass (mensaje) {
      const type = mensaje && mensaje.sender_type
      if (type === 'cliente' || type === 'admin_propiedad') return 'from-client'
      if (type === 'profesional') return 'from-professional'
      if (type === 'administrador') return 'from-admin'
      return 'from-system'
    },
    messageText (mensaje) {
      if (!mensaje) return ''
      return mensaje.mensaje || mensaje.mensaje_original || 'Mensaje sin texto'
    },
    messagePhotos (mensaje) {
      return mensaje && Array.isArray(mensaje.fotos) ? mensaje.fotos : []
    },
    authorName (comentario) {
      if (!comentario || !comentario.autor) return 'Sistema'
      return `${comentario.autor.nombre || ''} ${comentario.autor.apellido || ''}`.trim() || 'Administrador'
    },
    formatCommentDate (parts) {
      const iso = this.commentDateToIso(parts)
      return this.formatDateTime(iso)
    },
    commentDateToIso (parts) {
      if (!parts) return null
      const date = new Date(parts.a, (parts.m || 1) - 1, parts.d || 1, parts.h || 0, parts.min || 0, parts.seg || 0)
      return date.toISOString()
    }
  }
}
</script>

<style scoped>
.column-right{
  background-color: rgb(242, 247, 251);
  padding-top:80px;
  padding-left:260px;
  width:100%;
  min-height:100vh;
}

.main-container{
  padding:22px;
}

.order-admin-page{
  color:#0f172a;
}

.order-admin-loader{
  min-height:360px;
  display:flex;
  align-items:center;
  justify-content:center;
  gap:12px;
  color:#64748b;
  font-weight:700;
}

.order-admin-header{
  display:flex;
  align-items:flex-start;
  justify-content:space-between;
  gap:18px;
  margin-bottom:18px;
}

.header-main{
  display:flex;
  align-items:flex-start;
  gap:14px;
  min-width:0;
}

.back-button{
  width:42px;
  height:42px;
  border-radius:12px;
  border:1px solid #dbe3ef;
  background:#fff;
  color:#334155;
  display:flex;
  align-items:center;
  justify-content:center;
  flex-shrink:0;
}

.eyebrow{
  font-size:11px;
  font-weight:800;
  text-transform:uppercase;
  color:#64748b;
}

h1{
  font-size:28px !important;
  line-height:1.15 !important;
  margin:2px 0 8px !important;
  font-weight:800 !important;
  color:#0f172a;
}

.header-meta{
  display:flex;
  align-items:center;
  gap:10px;
  flex-wrap:wrap;
  color:#64748b;
  font-size:13px;
  font-weight:600;
}

.header-link{
  color:#0369a1;
  font-weight:800;
  text-decoration:none;
}

.header-actions{
  display:flex;
  align-items:center;
  gap:8px;
  flex-wrap:wrap;
  justify-content:flex-end;
}

.action-button{
  min-height:40px;
  border-radius:10px;
  padding:0 13px;
  display:inline-flex;
  align-items:center;
  gap:7px;
  font-weight:700;
  font-size:13px;
  text-decoration:none;
  border:1px solid #dbe3ef;
}

.action-button.primary{
  background:#0f172a;
  color:#fff;
  border-color:#0f172a;
}

.action-button.secondary{
  background:#fff;
  color:#0f172a;
}

.summary-grid{
  display:grid;
  grid-template-columns:repeat(4, minmax(0, 1fr));
  gap:12px;
  margin-bottom:18px;
}

.summary-card,
.panel{
  background:#fff;
  border:1px solid #e2e8f0;
  border-radius:8px;
  box-shadow:0 8px 22px rgba(15,23,42,0.05);
}

.summary-card{
  padding:14px;
  display:flex;
  align-items:center;
  gap:12px;
  min-width:0;
}

.summary-card:nth-child(1){
  border-top:3px solid #38bdf8;
}

.summary-card:nth-child(2){
  border-top:3px solid #818cf8;
}

.summary-card:nth-child(3){
  border-top:3px solid #22c55e;
}

.summary-card:nth-child(4){
  border-top:3px solid #f59e0b;
}

.summary-icon{
  width:38px;
  height:38px;
  border-radius:10px;
  display:flex;
  align-items:center;
  justify-content:center;
  flex:0 0 38px;
  font-size:17px;
}

.summary-icon.customer{
  background:#e0f2fe;
  color:#0369a1;
}

.summary-icon.professional{
  background:#e0e7ff;
  color:#4338ca;
}

.summary-icon.schedule{
  background:#dcfce7;
  color:#15803d;
}

.summary-icon.money{
  background:#fef3c7;
  color:#b45309;
}

.summary-copy{
  min-width:0;
  display:flex;
  flex-direction:column;
  gap:4px;
}

.summary-label,
.detail-line span{
  font-size:11px;
  color:#64748b;
  font-weight:800;
  text-transform:uppercase;
}

.summary-copy strong{
  font-size:16px;
  color:#0f172a;
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
}

.summary-copy span:last-child{
  font-size:12px;
  color:#64748b;
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
}

.content-grid{
  display:grid;
  grid-template-columns:minmax(0, 1fr) 360px;
  gap:18px;
  align-items:start;
}

.content-main,
.content-side{
  display:flex;
  flex-direction:column;
  gap:14px;
}

.panel{
  padding:16px;
}

.panel-header{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  margin-bottom:12px;
}

.panel-header h2{
  font-size:16px !important;
  line-height:1.2 !important;
  margin:0 !important;
  font-weight:800 !important;
  display:flex;
  align-items:center;
  gap:8px;
}

.panel-title-icon{
  width:28px;
  height:28px;
  border-radius:8px;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  flex:0 0 28px;
  font-size:14px;
}

.timeline-icon,
.comments-icon{
  background:#f1f5f9;
  color:#475569;
}

.services-icon,
.budget-icon,
.request-icon{
  background:#e0f2fe;
  color:#0369a1;
}

.visits-icon,
.customer-icon{
  background:#dcfce7;
  color:#15803d;
}

.professional-icon{
  background:#e0e7ff;
  color:#4338ca;
}

.conversation-icon{
  background:#ecfeff;
  color:#0e7490;
}

.payments-icon{
  background:#fef3c7;
  color:#b45309;
}

.refunds-icon{
  background:#fee2e2;
  color:#b91c1c;
}

.files-icon{
  background:#fee2e2;
  color:#b91c1c;
}

.panel-header span{
  color:#64748b;
  font-size:12px;
  font-weight:700;
}

.table-wrap{
  overflow-x:auto;
}

.admin-table{
  width:100%;
  border-collapse:collapse;
  font-size:13px;
}

.admin-table th{
  color:#64748b;
  font-size:11px;
  text-transform:uppercase;
  padding:10px 8px;
  border-bottom:1px solid #e2e8f0;
}

.admin-table td{
  padding:11px 8px;
  border-bottom:1px solid #f1f5f9;
  vertical-align:top;
}

.admin-table td small{
  display:block;
  color:#64748b;
  margin-top:2px;
}

.text-right{
  text-align:right;
}

.status-badge,
.mini-badge{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  border-radius:999px;
  font-size:11px;
  font-weight:800;
  line-height:1;
  white-space:nowrap;
}

.status-badge{
  padding:6px 10px;
}

.mini-badge{
  padding:5px 8px;
  margin:2px 3px 2px 0;
}

.primary{ background:#dbeafe; color:#1d4ed8; }
.info{ background:#e0f2fe; color:#0369a1; }
.success{ background:#dcfce7; color:#15803d; }
.warning{ background:#fef3c7; color:#b45309; }
.danger{ background:#fee2e2; color:#b91c1c; }
.muted{ background:#f1f5f9; color:#64748b; }

.stack-list,
.comments-list,
.asset-list{
  display:flex;
  flex-direction:column;
  gap:10px;
}

.stack-list.compact{
  gap:8px;
}

.list-row{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  border:1px solid #edf2f7;
  border-radius:8px;
  padding:11px;
}

.list-row div{
  min-width:0;
  display:flex;
  flex-direction:column;
  gap:2px;
}

.list-row strong{
  font-size:13px;
}

.list-row span{
  font-size:12px;
  color:#64748b;
}

.budget-row{
  align-items:flex-start;
}

.row-right{
  align-items:flex-end;
  text-align:right;
  flex-shrink:0;
}

.timeline{
  display:flex;
  flex-direction:column;
  gap:12px;
}

.timeline-item{
  display:grid;
  grid-template-columns:18px minmax(0, 1fr);
  gap:10px;
}

.timeline-dot{
  width:10px;
  height:10px;
  border-radius:50%;
  margin-top:5px;
}

.timeline-title{
  font-size:13px;
  font-weight:800;
}

.timeline-date{
  color:#64748b;
  font-size:11px;
  font-weight:700;
}

.timeline p,
.description-text,
.comment-card p{
  margin:4px 0 0;
  color:#475569;
  font-size:12px;
  line-height:1.45;
}

.request-conversation{
  display:flex;
  flex-direction:column;
  gap:10px;
  max-height:560px;
  overflow-y:auto;
  padding:2px 4px 2px 0;
}

.conversation-tabs{
  display:flex;
  flex-wrap:wrap;
  gap:8px;
  margin-bottom:12px;
}

.conversation-tab{
  min-height:34px;
  border:1px solid #dbe3ef;
  border-radius:8px;
  background:#fff;
  color:#334155;
  display:inline-flex;
  align-items:center;
  gap:8px;
  padding:0 10px;
  font-size:12px;
  font-weight:800;
  cursor:pointer;
}

.conversation-tab small{
  min-width:20px;
  height:20px;
  border-radius:999px;
  background:#f1f5f9;
  color:#64748b;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  font-size:11px;
  font-weight:800;
}

.conversation-tab.active{
  background:#0f172a;
  border-color:#0f172a;
  color:#fff;
}

.conversation-tab.active small{
  background:rgba(255,255,255,0.16);
  color:#fff;
}

.conversation-message{
  width:88%;
  border-radius:12px;
  border:1px solid #e2e8f0;
  padding:11px 12px;
  background:#fff;
}

.conversation-message.from-client{
  align-self:flex-start;
  background:#f8fafc;
  border-color:#cbd5e1;
}

.conversation-message.from-professional{
  align-self:flex-end;
  background:#eef2ff;
  border-color:#c7d2fe;
}

.conversation-message.from-admin{
  align-self:flex-end;
  background:#ecfeff;
  border-color:#a5f3fc;
}

.conversation-message.from-system{
  align-self:center;
  background:#f1f5f9;
  border-color:#e2e8f0;
}

.message-heading{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:10px;
  margin-bottom:6px;
}

.message-heading span{
  font-size:11px;
  font-weight:800;
  color:#0f172a;
  text-transform:uppercase;
}

.message-heading time{
  color:#64748b;
  font-size:11px;
  font-weight:700;
  white-space:nowrap;
}

.conversation-message p{
  color:#334155;
  font-size:13px;
  line-height:1.45;
  margin:0;
  white-space:pre-wrap;
}

.message-photos{
  display:flex;
  flex-wrap:wrap;
  gap:8px;
  margin-top:9px;
}

.message-photos a{
  display:inline-flex;
  align-items:center;
  gap:6px;
  border-radius:999px;
  background:#ffffff;
  border:1px solid #cbd5e1;
  color:#0369a1;
  font-size:12px;
  font-weight:800;
  padding:6px 9px;
  text-decoration:none;
}

.message-original{
  margin-top:8px;
  padding-top:8px;
  border-top:1px dashed rgba(100,116,139,0.35);
  color:#64748b;
  font-size:11px;
  line-height:1.4;
}

.contact-panel{
  display:flex;
  flex-direction:column;
  gap:7px;
}

.contact-name{
  font-size:17px;
  font-weight:800;
}

.contact-panel a,
.inline-link,
.asset-list a{
  color:#0369a1;
  font-size:13px;
  font-weight:700;
  text-decoration:none;
  word-break:break-word;
}

.detail-line{
  display:flex;
  flex-direction:column;
  gap:2px;
  margin-top:6px;
}

.detail-line strong{
  font-size:13px;
  color:#0f172a;
}

.flag-list{
  display:flex;
  flex-wrap:wrap;
  gap:4px;
}

.comment-card{
  border:1px solid #edf2f7;
  border-radius:8px;
  padding:10px;
}

.comment-card div{
  display:flex;
  justify-content:space-between;
  gap:8px;
  color:#64748b;
  font-size:11px;
}

.comment-card strong{
  color:#0f172a;
  font-size:12px;
}

.asset-list a,
.asset-list span{
  display:flex;
  align-items:center;
  gap:8px;
  color:#334155;
  font-size:13px;
  font-weight:700;
}

.empty-state{
  border:1px dashed #cbd5e1;
  border-radius:8px;
  padding:18px;
  text-align:center;
  color:#64748b;
  font-size:13px;
  font-weight:700;
}

@media (max-width: 1100px) {
  .summary-grid{
    grid-template-columns:repeat(2, minmax(0, 1fr));
  }
  .content-grid{
    grid-template-columns:1fr;
  }
}

@media (max-width: 800px) {
  .column-right{
    padding-left:0;
  }
  .order-admin-header{
    flex-direction:column;
  }
  .header-actions{
    justify-content:flex-start;
  }
}

@media (max-width: 640px) {
  .main-container{
    padding:14px;
  }
  .summary-grid{
    grid-template-columns:1fr;
  }
  h1{
    font-size:23px !important;
  }
  .list-row{
    align-items:flex-start;
    flex-direction:column;
  }
  .row-right{
    align-items:flex-start;
    text-align:left;
  }
}
</style>
